/**
 * Pack Variant Builder - Handles 3 display modes: no_builder, predefined_builder, full_builder
 */

class PackVariantBuilder {
  constructor() {
    this.container = document.querySelector('[data-pack-variant-builder]');
    if (!this.container) return;

    // Expose instance on DOM element for product-template.js
    this.container.__instance = this;

    const configEl = this.container.querySelector('[data-pack-variant-config]');
    if (!configEl) return;

    this.config = JSON.parse(configEl.textContent);
    this.labels = this.config.labels;
    this.variants = this.config.variants;

    // State
    this.selectedVariantId = null;
    this.activeTabIndex = 0;
    this.selections = new Map(); // Map<categoryKey, Map<productHandle, qty>>
    this.dropdownSelections = new Map(); // Map<slotIndex, productHandle>

    // DOM
    this.contentArea = this.container.querySelector('[data-builder-content]');
    this.contentInput = this.container.querySelector('[data-pack-content-input]');
    this.idsInput = this.container.querySelector('[data-pack-ids-input]');
    this.modeInput = this.container.querySelector('[data-pack-mode-input]');

    this.init();
  }

  init() {
    this.bindVariantCards();
    this.bindFormSubmit();
    this.initSelectedVariant();
  }

  // ==========================================================================
  // Price Formatting
  // ==========================================================================

  formatPrice(cents) {
    return window.formatPrice(cents);
  }

  // ==========================================================================
  // Variant Selection
  // ==========================================================================

  bindVariantCards() {
    const cards = this.container.querySelectorAll('.pack-variant-builder__card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.disabled) return;
        const variantId = parseInt(card.dataset.variantId, 10);
        this.selectVariant(variantId);
      });
    });
    cards[1]?.click();
  }

  initSelectedVariant() {
    const activeCard = this.container.querySelector('.pack-variant-builder__card--active');
    if (activeCard) {
      this.selectVariant(parseInt(activeCard.dataset.variantId, 10));
    } else if (this.variants.length > 0) {
      this.selectVariant(this.variants[0].id);
    }
  }

  selectVariant(variantId) {
    this.selectedVariantId = variantId;
    const variant = this.getSelectedVariant();
    if (!variant) return;

    // Reset state
    this.selections.clear();
    this.dropdownSelections.clear();
    this.activeTabIndex = 0;

    // Update card active states
    const cards = this.container.querySelectorAll('.pack-variant-builder__card');
    cards.forEach(card => {
      const id = parseInt(card.dataset.variantId, 10);
      card.classList.toggle('pack-variant-builder__card--active', id === variantId);
    });

    // Update URL
    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    // Update submit button variant
    this.updateMainFormVariant(variant);

    // Update mode input
    if (this.modeInput) {
      this.modeInput.value = variant.displayMode;
    }

    // Update product media first slide image
    this.updateProductImage(variant);

    // Render builder content
    this.renderContent(variant);
  }

  updateProductImage(variant) {
    if (!variant.featuredImage) return;

    const variantSlide = document.querySelector('[data-variant-slide] img');
    if (variantSlide) {
      variantSlide.src = variant.featuredImage;
      variantSlide.srcset = '';
    }

    const variantThumb = document.querySelector('[data-variant-thumb] img');
    if (variantThumb) {
      variantThumb.src = variant.featuredImageThumb || variant.featuredImage;
      variantThumb.srcset = '';
    }

    // Slide back to first slide
    const swiperEl = document.querySelector('.product-media__slider');
    if (swiperEl?.swiper) {
      swiperEl.swiper.slideTo(0);
    }
  }

  getSelectedVariant() {
    return this.variants.find(v => v.id === this.selectedVariantId) || null;
  }

  updateMainFormVariant(variant) {
    const submitBtn = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (!submitBtn) return;

    submitBtn.setAttribute('variant-id', variant.id);
    submitBtn.setAttribute('data-original-price', variant.price);
    submitBtn.setAttribute('data-compare-price', variant.compareAtPrice || variant.price);

    const priceEl = submitBtn.querySelector('.product-form__price');
    const comparePriceEl = submitBtn.querySelector('.product-form__compare-price');

    if (priceEl) priceEl.textContent = this.formatPrice(variant.price);
    if (comparePriceEl) {
      comparePriceEl.textContent = this.formatPrice(variant.compareAtPrice);
      const hasDiscount = variant.compareAtPrice > variant.price;
      if (comparePriceEl.parentElement) {
        comparePriceEl.parentElement.style.display = hasDiscount ? '' : 'none';
      }
    }

    // Update subscription picker prices
    const productFormEl = document.querySelector('product-form');
    if (productFormEl && typeof productFormEl.updateSubscriptionPickerPrices === 'function') {
      productFormEl.updateSubscriptionPickerPrices({ id: variant.id, price: variant.price, compare_at_price: variant.compareAtPrice });
    }

    // Update sticky bar product info
    this.updateStickyProductInfo(variant);
  }

  updateStickyProductInfo(variant) {
    const titleEl = document.querySelector('[data-pack-variant-sticky-title]');
    const subtitleEl = document.querySelector('[data-pack-variant-sticky-subtitle]');

    if (titleEl) titleEl.textContent = variant.label || '';
    if (subtitleEl) subtitleEl.textContent = variant.subtitle || '';
  }

  // ==========================================================================
  // Content Rendering
  // ==========================================================================

  renderContent(variant) {
    if (!this.contentArea) return;

    switch (variant.displayMode) {
      case 'no_builder':
        this.renderNoBuilder(variant);
        break;
      case 'predefined_builder':
        this.renderPredefinedBuilder(variant);
        break;
      case 'full_builder':
        this.renderFullBuilder(variant);
        break;
      default:
        this.renderNoBuilder(variant);
    }

    this.bindModals();
    this.updateSubmitButton();
  }

  bindModals() {
    const productTemplate = this.container.closest('product-form');
    if (!productTemplate || typeof productTemplate.openModal !== 'function') return;

    this.contentArea.querySelectorAll('[data-open-modal]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.productHandle;
        if (handle) productTemplate.openModal(handle);
      });
    });
  }

  // --------------------------------------------------------------------------
  // no_builder mode
  // --------------------------------------------------------------------------

  renderNoBuilder(variant) {
    let html = '';

    // Header
    html += `<div class="pack-variant-builder__content-header">
      <p class="pack-variant-builder__content-label p-small fw-6">${this.labels.contentLabel}</p>
    </div>`;

    html += '<div class="pack-variant-builder__items">';

    // Fixed items
    if (variant.fixedItems?.length) {
      variant.fixedItems.forEach(fi => {
        const imgSrc = fi.image || fi.featuredImage || '';
        html += this.renderFixedItemHTML(fi, imgSrc);
      });
    }

    // Dropdown slots for each category
    if (variant.categories) {
      variant.categories.forEach((cat, catIndex) => {
        const dp = cat.displayProduct;
        const dpImg = dp ? (dp.image || dp.featuredImage || '') : '';
        for (let i = 0; i < cat.limit; i++) {
          const slotIndex = `${catIndex}_${i}`;
          html += `<div class="pack-variant-builder__item" data-dropdown-slot="${slotIndex}" data-category-index="${catIndex}">
            <div class="pack-variant-builder__item-image" data-dropdown-image>
              ${dpImg ? `<img src="${dpImg}" alt="${this.escapeHtml(dp.title)}" loading="lazy">` : ''}
            </div>
            <div class="pack-variant-builder__item-info">
              <div class="pack-variant-builder__item-title-row">
                <p class="pack-variant-builder__item-title p-medium fw-6" data-dropdown-title>${dp ? this.escapeHtml(dp.title) : cat.label + ' ' + (i + 1)}</p>
                <div class="pack-variant-builder__item-dropdown">
                  <button type="button" class="pack-variant-builder__dropdown-trigger p-small fw-7" data-dropdown-toggle="${slotIndex}">
                    <span class="pack-variant-builder__dropdown-trigger-text">${this.labels.dropdownPlaceholder}</span>
                    <svg class="pack-variant-builder__dropdown-trigger-icon" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 0.5L5 4.5L9 0.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <div class="pack-variant-builder__dropdown-list" data-dropdown-list="${slotIndex}">
                    ${cat.products.map(p => {
                      const pImg = p.image || p.featuredImage || '';
                      return `<button type="button" class="pack-variant-builder__dropdown-option p-small" data-dropdown-option data-handle="${p.handle}" data-variant-id="${p.variantId}" data-slot="${slotIndex}">
                        <div class="pack-variant-builder__dropdown-option-image">
                          ${pImg ? `<img src="${pImg}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : ''}
                        </div>
                        <span>${this.escapeHtml(p.title)}</span>
                      </button>`;
                    }).join('')}
                  </div>
                </div>
              </div>
              ${dp?.description ? `<p class="pack-variant-builder__item-desc p-extra-small">${this.escapeHtml(dp.description)}</p>` : ''}
              ${dp ? `<button type="button" class="pack-variant-builder__item-details p-extra-small fw-6" data-open-modal="product-modal" data-product-handle="${dp.handle}">${this.labels.detailsLabel}</button>` : ''}
            </div>
            <span class="pack-variant-builder__item-qty">x1</span>
          </div>`;
        }
      });
    }

    html += '</div>';
    this.contentArea.innerHTML = html;
    this.bindDropdowns();

    // Apply default selections to dropdowns
    if (variant.categories) {
      variant.categories.forEach((cat, catIndex) => {
        let slotIdx = 0;
        cat.products.forEach(p => {
          const dq = p.defaultQty || 0;
          for (let i = 0; i < dq && slotIdx < cat.limit; i++) {
            const slotIndex = `${catIndex}_${slotIdx}`;
            this.selectDropdownOption(slotIndex, p.handle);
            slotIdx++;
          }
        });
      });
    }
  }

  bindDropdowns() {
    // Close all dropdowns when clicking outside (bind once)
    if (!this._dropdownOutsideClickBound) {
      this._dropdownOutsideClickBound = true;
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.pack-variant-builder__item-dropdown')) {
          this.closeAllDropdowns();
        }
      });
    }

    // Toggle dropdowns
    this.contentArea.querySelectorAll('[data-dropdown-toggle]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const slotIndex = trigger.dataset.dropdownToggle;
        const list = this.contentArea.querySelector(`[data-dropdown-list="${slotIndex}"]`);
        const isOpen = list?.classList.contains('pack-variant-builder__dropdown-list--open');

        this.closeAllDropdowns();

        if (!isOpen && list) {
          list.classList.add('pack-variant-builder__dropdown-list--open');
          trigger.classList.add('pack-variant-builder__dropdown-trigger--open');
        }
      });
    });

    // Option selection
    this.contentArea.querySelectorAll('[data-dropdown-option]').forEach(option => {
      option.addEventListener('click', () => {
        const handle = option.dataset.handle;
        const slotIndex = option.dataset.slot;
        this.selectDropdownOption(slotIndex, handle);
      });
    });
  }

  closeAllDropdowns() {
    this.contentArea?.querySelectorAll('.pack-variant-builder__dropdown-list--open').forEach(list => {
      list.classList.remove('pack-variant-builder__dropdown-list--open');
    });
    this.contentArea?.querySelectorAll('.pack-variant-builder__dropdown-trigger--open').forEach(trigger => {
      trigger.classList.remove('pack-variant-builder__dropdown-trigger--open');
    });
  }

  selectDropdownOption(slotIndex, handle) {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    this.dropdownSelections.set(slotIndex, handle);

    // Find the product data
    const catIndex = parseInt(slotIndex.split('_')[0], 10);
    const cat = variant.categories[catIndex];
    const product = cat?.products.find(p => p.handle === handle);

    if (!product) return;

    // Update slot UI
    const slot = this.contentArea.querySelector(`[data-dropdown-slot="${slotIndex}"]`);
    if (slot) {
      const imgEl = slot.querySelector('[data-dropdown-image]');
      const triggerText = slot.querySelector('.pack-variant-builder__dropdown-trigger-text');

      const imgSrc = product.image || product.featuredImage || '';
      if (imgEl) imgEl.innerHTML = imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : '';
      if (triggerText) triggerText.textContent = product.title;

      // Update details button handle
      const detailsBtn = slot.querySelector('.pack-variant-builder__item-details');
      if (detailsBtn) detailsBtn.dataset.productHandle = product.handle;

      // Mark selected option
      slot.querySelectorAll('[data-dropdown-option]').forEach(opt => {
        opt.classList.toggle('pack-variant-builder__dropdown-option--selected', opt.dataset.handle === handle);
      });
    }

    this.closeAllDropdowns();
    this.updateFormInputs();
    this.updateSubmitButton();
  }

  // --------------------------------------------------------------------------
  // predefined_builder mode
  // --------------------------------------------------------------------------

  renderPredefinedBuilder(variant) {
    let html = '';
    const cat = variant.categories[0];
    const limit = cat ? cat.limit : 0;

    // Initialize selections for this category with defaults
    let initialTotal = 0;
    if (cat) {
      const catSelections = new Map();
      cat.products.forEach(p => {
        const dq = p.defaultQty || 0;
        catSelections.set(p.handle, dq);
        initialTotal += dq;
      });
      this.selections.set(cat.key, catSelections);
    }

    // Header with counter
    html += `<div class="pack-variant-builder__content-header">
      <p class="pack-variant-builder__content-label p-small fw-6">${this.labels.contentLabel}</p>
      <div class="pack-variant-builder__counter" data-counter>
        <span class="pack-variant-builder__counter-current p-small fw-7" data-counter-current>${initialTotal}</span>
        <span class="pack-variant-builder__counter-separator p-extra-small">/</span>
        <span class="pack-variant-builder__counter-max p-extra-small" data-counter-max>${limit} ${this.labels.counterSuffix}</span>
      </div>
    </div>`;

    html += '<div class="pack-variant-builder__items">';

    // Fixed items
    if (variant.fixedItems?.length) {
      variant.fixedItems.forEach(fi => {
        const imgSrc = fi.image || fi.featuredImage || '';
        html += this.renderFixedItemHTML(fi, imgSrc);
      });
    }

    // Flavor products with +/- controls
    if (cat) {
      const qtyPerPack = cat.qtyPerPack || 1;
      cat.products.forEach(p => {
        const imgSrc = p.image || p.featuredImage || '';
        html += this.renderQtyItemHTML(p, imgSrc, cat.key, qtyPerPack);
      });
    }

    html += '</div>';
    this.contentArea.innerHTML = html;
    this.bindQtyControls();
    this.updateQtyButtonsState();
    this.updateFormInputs();
  }

  // --------------------------------------------------------------------------
  // full_builder mode
  // --------------------------------------------------------------------------

  renderFullBuilder(variant) {
    let html = '';

    // Initialize selections for all categories with defaults
    let initialTotal = 0;
    variant.categories.forEach(cat => {
      const catSelections = new Map();
      cat.products.forEach(p => {
        const dq = p.defaultQty || 0;
        catSelections.set(p.handle, dq);
        initialTotal += dq;
      });
      this.selections.set(cat.key, catSelections);
    });

    // Get total limit for counter
    const totalLimit = variant.categories.reduce((sum, c) => sum + c.limit, 0);

    // Header with counter
    html += `<div class="pack-variant-builder__content-header">
      <p class="pack-variant-builder__content-label p-small fw-6">${this.labels.contentLabel}</p>
      <div class="pack-variant-builder__counter" data-counter>
        <span class="pack-variant-builder__counter-current p-small fw-7" data-counter-current>${initialTotal}</span>
        <span class="pack-variant-builder__counter-separator p-extra-small">/</span>
        <span class="pack-variant-builder__counter-max p-extra-small" data-counter-max>${totalLimit} ${this.labels.counterSuffix}</span>
      </div>
    </div>`;

    // Tabs
    if (variant.categories.length > 1) {
      html += '<div class="pack-variant-builder__tabs">';
      variant.categories.forEach((cat, index) => {
        const activeClass = index === 0 ? ' pack-variant-builder__tab--active' : '';
        html += `<button type="button" class="pack-variant-builder__tab p-large fw-7${activeClass}" data-tab-index="${index}">
          ${this.escapeHtml(cat.tabLabel || cat.label)}
        </button>`;
      });
      html += '</div>';
    }

    // Category panels
    variant.categories.forEach((cat, catIndex) => {
      const displayStyle = catIndex === 0 ? '' : ' style="display: none;"';
      html += `<div class="pack-variant-builder__items" data-category-panel="${catIndex}"${displayStyle}>`;

      // Fixed items only on first tab
      if (catIndex === 0 && variant.fixedItems?.length) {
        variant.fixedItems.forEach(fi => {
          const imgSrc = fi.image || fi.featuredImage || '';
          html += this.renderFixedItemHTML(fi, imgSrc);
        });
      }

      const qtyPerPack = cat.qtyPerPack || 1;
      cat.products.forEach(p => {
        const imgSrc = p.image || p.featuredImage || '';
        html += this.renderQtyItemHTML(p, imgSrc, cat.key, qtyPerPack);
      });

      html += '</div>';
    });

    this.contentArea.innerHTML = html;
    this.bindTabs();
    this.bindQtyControls();
    this.updateQtyButtonsState();
    this.updateFormInputs();
  }

  // --------------------------------------------------------------------------
  // Shared HTML renderers
  // --------------------------------------------------------------------------

  renderFixedItemHTML(fi, imgSrc) {
    return `<div class="pack-variant-builder__item pack-variant-builder__item--fixed">
      <div class="pack-variant-builder__item-image">
        ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(fi.title)}" loading="lazy">` : ''}
      </div>
      <div class="pack-variant-builder__item-info">
        <p class="pack-variant-builder__item-title p-medium fw-6">${this.escapeHtml(fi.title)}</p>
        ${fi.description ? `<p class="pack-variant-builder__item-desc p-extra-small">${this.escapeHtml(fi.description)}</p>` : ''}
        <button type="button" class="pack-variant-builder__item-details p-extra-small fw-6" data-open-modal="product-modal" data-product-handle="${fi.handle}">
          ${this.labels.detailsLabel}
        </button>
      </div>
      <span class="pack-variant-builder__item-qty p-medium fw-6">x${fi.qty}</span>
    </div>`;
  }

  renderQtyItemHTML(p, imgSrc, categoryKey, qtyPerPack) {
    const dq = p.defaultQty || 0;
    const displayQty = dq * qtyPerPack;
    return `<div class="pack-variant-builder__item" data-product-handle="${p.handle}" data-category-key="${categoryKey}">
      <div class="pack-variant-builder__item-image">
        ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : ''}
      </div>
      <div class="pack-variant-builder__item-info">
        <p class="pack-variant-builder__item-title p-medium fw-6">${this.escapeHtml(p.title)}</p>
        ${p.description ? `<p class="pack-variant-builder__item-desc p-extra-small">${this.escapeHtml(p.description)}</p>` : ''}
        <button type="button" class="pack-variant-builder__item-details p-extra-small fw-6" data-open-modal="product-modal" data-product-handle="${p.handle}">
          ${this.labels.detailsLabel}
        </button>
      </div>
      <div class="pack-variant-builder__item-controls" data-controls-handle="${p.handle}" data-controls-category="${categoryKey}" data-qty-per-pack="${qtyPerPack}">
        <button type="button" class="pack-variant-builder__item-controls-btn pack-variant-builder__item-controls-btn--minus" data-action="decrease" aria-label="Remove">
          <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1H13" stroke="#8143C2" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="pack-variant-builder__item-controls-qty" data-qty-display>${displayQty}</span>
        <button type="button" class="pack-variant-builder__item-controls-btn pack-variant-builder__item-controls-btn--plus" data-action="increase" aria-label="Add">+${qtyPerPack}</button>
      </div>
    </div>`;
  }

  // ==========================================================================
  // Tabs
  // ==========================================================================

  bindTabs() {
    this.contentArea.querySelectorAll('[data-tab-index]').forEach(tab => {
      tab.addEventListener('click', () => {
        const index = parseInt(tab.dataset.tabIndex, 10);
        this.switchTab(index);
      });
    });
  }

  switchTab(index) {
    this.activeTabIndex = index;

    // Update tab active states
    this.contentArea.querySelectorAll('[data-tab-index]').forEach(tab => {
      const tabIndex = parseInt(tab.dataset.tabIndex, 10);
      tab.classList.toggle('pack-variant-builder__tab--active', tabIndex === index);
    });

    // Show/hide panels
    this.contentArea.querySelectorAll('[data-category-panel]').forEach(panel => {
      const panelIndex = parseInt(panel.dataset.categoryPanel, 10);
      panel.style.display = panelIndex === index ? '' : 'none';
    });
  }

  // ==========================================================================
  // Qty Controls
  // ==========================================================================

  bindQtyControls() {
    this.contentArea.querySelectorAll('[data-action="increase"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const controls = btn.closest('[data-controls-handle]');
        if (!controls) return;
        this.changeQty(controls.dataset.controlsHandle, controls.dataset.controlsCategory, 1);
      });
    });

    this.contentArea.querySelectorAll('[data-action="decrease"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const controls = btn.closest('[data-controls-handle]');
        if (!controls) return;
        this.changeQty(controls.dataset.controlsHandle, controls.dataset.controlsCategory, -1);
      });
    });
  }

  changeQty(handle, categoryKey, delta) {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    const catSelections = this.selections.get(categoryKey);
    if (!catSelections) return;

    const currentQty = catSelections.get(handle) || 0;
    const cat = variant.categories.find(c => c.key === categoryKey);
    if (!cat) return;

    // Check limits
    if (delta > 0) {
      const catTotal = this.getCategoryTotal(categoryKey);
      if (catTotal >= cat.limit) return;
    }

    if (delta < 0 && currentQty <= 0) return;

    const newQty = Math.max(0, currentQty + delta);
    catSelections.set(handle, newQty);

    // Update display (show qty * qtyPerPack for visual)
    const controlsEl = this.contentArea.querySelector(`[data-controls-handle="${handle}"][data-controls-category="${categoryKey}"]`);
    if (controlsEl) {
      const qtyPerPack = parseInt(controlsEl.dataset.qtyPerPack, 10) || 1;
      const qtyDisplay = controlsEl.querySelector('[data-qty-display]');
      if (qtyDisplay) qtyDisplay.textContent = newQty * qtyPerPack;
    }

    this.updateCounter();
    this.updateQtyButtonsState();
    this.updateFormInputs();
    this.updateSubmitButton();
  }

  getCategoryTotal(categoryKey) {
    const catSelections = this.selections.get(categoryKey);
    if (!catSelections) return 0;
    let total = 0;
    catSelections.forEach(qty => { total += qty; });
    return total;
  }

  getTotalSelected() {
    let total = 0;
    this.selections.forEach(catSelections => {
      catSelections.forEach(qty => { total += qty; });
    });
    return total;
  }

  getTotalLimit() {
    const variant = this.getSelectedVariant();
    if (!variant) return 0;
    return variant.categories.reduce((sum, c) => sum + c.limit, 0);
  }

  updateCounter() {
    const currentEl = this.contentArea?.querySelector('[data-counter-current]');
    if (currentEl) {
      currentEl.textContent = this.getTotalSelected();
    }
  }

  updateQtyButtonsState() {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    variant.categories.forEach(cat => {
      const catTotal = this.getCategoryTotal(cat.key);
      const isMaxReached = catTotal >= cat.limit;

      this.contentArea?.querySelectorAll(`[data-controls-category="${cat.key}"] [data-action="increase"]`).forEach(btn => {
        btn.disabled = isMaxReached;
      });
    });
  }

  // ==========================================================================
  // Form Inputs
  // ==========================================================================

  updateFormInputs() {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    const contentParts = [];
    const idParts = [];

    // Fixed items
    if (variant.fixedItems?.length) {
      variant.fixedItems.forEach(fi => {
        contentParts.push(`${fi.qty}x ${fi.title}`);
        idParts.push(`${fi.variantId}:${fi.qty}`);
      });
    }

    if (variant.displayMode === 'no_builder') {
      // Aggregate dropdown selections by handle
      const handleCounts = new Map();
      this.dropdownSelections.forEach((handle) => {
        handleCounts.set(handle, (handleCounts.get(handle) || 0) + 1);
      });
      handleCounts.forEach((count, handle) => {
        // Find the product in any category
        let product = null;
        for (const cat of variant.categories) {
          product = cat.products.find(p => p.handle === handle);
          if (product) break;
        }
        if (product) {
          contentParts.push(`${count}x ${product.title}`);
          idParts.push(`${product.variantId}:${count}`);
        }
      });
    } else {
      // Qty selections (predefined_builder and full_builder)
      this.selections.forEach((catSelections, categoryKey) => {
        const cat = variant.categories.find(c => c.key === categoryKey);
        const qtyPerPack = cat?.qtyPerPack || 1;
        catSelections.forEach((qty, handle) => {
          if (qty > 0) {
            const product = cat?.products.find(p => p.handle === handle);
            if (product) {
              const displayQty = qty * qtyPerPack;
              contentParts.push(`${displayQty}x ${product.title}`);
              idParts.push(`${product.variantId}:${qty}`);
            }
          }
        });
      });
    }

    const contentValue = contentParts.join(', ');
    const idsValue = idParts.join(',');

    if (this.contentInput) this.contentInput.value = contentValue;
    if (this.idsInput) this.idsInput.value = idsValue;

    // Sync properties to submit button for product-template.js
    const submitBtn = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (submitBtn) {
      const properties = {
        'Contenu': contentValue,
        '_display_mode': variant.displayMode
      };
      submitBtn.setAttribute('data-properties', JSON.stringify(properties));
    }
  }

  // ==========================================================================
  // Submit Button
  // ==========================================================================

  updateSubmitButton() {
    const submitBtn = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (!submitBtn) return;

    const variant = this.getSelectedVariant();
    if (!variant) return;

    const isComplete = this.isPackComplete(variant);
    const textWrapper = submitBtn.querySelector('.fw-6:not([variant-price-change])');
    const separator = submitBtn.querySelector('.submit-separator');
    const priceWrapper = submitBtn.querySelector('[variant-price-change]');

    // Save original button HTML on first call
    if (textWrapper && !this._originalButtonHTML) {
      this._originalButtonHTML = textWrapper.innerHTML;
    }

    if (isComplete) {
      submitBtn.disabled = false;
      if (textWrapper && this._originalButtonHTML) textWrapper.innerHTML = this._originalButtonHTML;
      if (separator) separator.style.display = '';
      if (priceWrapper) priceWrapper.style.display = '';
    } else {
      submitBtn.disabled = true;
      if (textWrapper) textWrapper.innerHTML = this.getIncompleteText(variant);
      if (separator) separator.style.display = 'none';
      if (priceWrapper) priceWrapper.style.display = 'none';
    }

    // Sync sticky bar CTA
    this.updateStickyButton(isComplete, submitBtn);
  }

  updateStickyButton(isComplete, submitBtn) {
    const stickyCta = document.querySelector('[data-pack-variant-sticky-cta]');
    if (!stickyCta) return;

    const stickyText = stickyCta.querySelector('[data-pack-variant-sticky-cta-text]');
    if (!stickyText) return;

    if (isComplete) {
      stickyCta.disabled = false;
      // Get desktop text only (avoid concatenating mobile + desktop)
      const desktopText = submitBtn.querySelector('.only-desktop')?.textContent?.trim() || '';
      const mainPrice = submitBtn.querySelector('.product-form__price')?.textContent?.trim() || '';
      stickyText.textContent = desktopText + (mainPrice ? ' — ' + mainPrice : '');
    } else {
      stickyCta.disabled = true;
      const variant = this.getSelectedVariant();
      stickyText.textContent = variant ? this.getIncompleteText(variant) : this.labels.incompleteText;
    }
  }

  isPackComplete(variant) {
    if (variant.displayMode === 'no_builder') {
      // Check all dropdown slots are filled
      let totalSlots = 0;
      variant.categories.forEach(cat => { totalSlots += cat.limit; });
      return this.dropdownSelections.size >= totalSlots;
    } else {
      // Check all category limits are met
      return variant.categories.every(cat => {
        return this.getCategoryTotal(cat.key) >= cat.limit;
      });
    }
  }

  getRemainingCount(variant) {
    if (variant.displayMode === 'no_builder') {
      let totalSlots = 0;
      variant.categories.forEach(cat => { totalSlots += cat.limit; });
      return Math.max(0, totalSlots - this.dropdownSelections.size);
    } else {
      const totalLimit = variant.categories.reduce((sum, c) => sum + c.limit, 0);
      return Math.max(0, totalLimit - this.getTotalSelected());
    }
  }

  getIncompleteText(variant) {
    const remaining = this.getRemainingCount(variant);
    if (remaining > 0 && this.labels.remainingText) {
      return this.labels.remainingText.replace('{count}', remaining);
    }
    return this.labels.incompleteText;
  }

  // ==========================================================================
  // Form Submit — delegate to product-template.js via data-properties
  // ==========================================================================

  bindFormSubmit() {
    // Listen for product:added to reset selections after add to cart
    if (this._submitBound) return;
    this._submitBound = true;

    document.addEventListener('product:added', () => {
      const variant = this.getSelectedVariant();
      if (variant) this.resetSelections(variant);
    });
  }

  // ==========================================================================
  // Reset after add to cart
  // ==========================================================================

  resetSelections(variant) {
    if (variant.displayMode === 'no_builder') {
      // Reset dropdowns then re-apply defaults
      this.dropdownSelections.clear();
      this.contentArea?.querySelectorAll('[data-dropdown-slot]').forEach(slot => {
        const triggerText = slot.querySelector('.pack-variant-builder__dropdown-trigger-text');
        if (triggerText) triggerText.textContent = this.labels.dropdownPlaceholder;
        slot.querySelectorAll('[data-dropdown-option]').forEach(opt => {
          opt.classList.remove('pack-variant-builder__dropdown-option--selected');
        });
      });
      // Re-apply default dropdown selections
      if (variant.categories) {
        variant.categories.forEach((cat, catIndex) => {
          let slotIdx = 0;
          cat.products.forEach(p => {
            const dq = p.defaultQty || 0;
            for (let i = 0; i < dq && slotIdx < cat.limit; i++) {
              const slotIndex = `${catIndex}_${slotIdx}`;
              this.selectDropdownOption(slotIndex, p.handle);
              slotIdx++;
            }
          });
        });
      }
    } else {
      // Reset qty selections to defaults
      variant.categories.forEach(cat => {
        const catSelections = this.selections.get(cat.key);
        if (!catSelections) return;
        const qtyPerPack = cat.qtyPerPack || 1;
        cat.products.forEach(p => {
          const dq = p.defaultQty || 0;
          catSelections.set(p.handle, dq);
          // Update qty display
          const controlsEl = this.contentArea?.querySelector(`[data-controls-handle="${p.handle}"][data-controls-category="${cat.key}"]`);
          if (controlsEl) {
            const qtyDisplay = controlsEl.querySelector('[data-qty-display]');
            if (qtyDisplay) qtyDisplay.textContent = dq * qtyPerPack;
          }
        });
      });
    }

    this.updateCounter();
    this.updateQtyButtonsState();
    this.updateFormInputs();
    this.updateSubmitButton();
  }

  // ==========================================================================
  // Recharge Bundle Selections
  // ==========================================================================

  getRechargeSelections(activePlanName) {
    const variant = this.getSelectedVariant();
    if (!variant) return [];

    const selections = [];

    // Fixed items
    if (variant.fixedItems?.length) {
      variant.fixedItems.forEach(fi => {
        const sellingPlanId = this.findSellingPlanByName(fi.sellingPlans, activePlanName);
        selections.push({
          externalProductId: String(fi.productId || ''),
          externalVariantId: String(fi.variantId),
          collectionId: String(fi.collectionId || ''),
          quantity: fi.qty,
          sellingPlan: String(sellingPlanId)
        });
      });
    }

    if (variant.displayMode === 'no_builder') {
      // Dropdown selections — aggregate by handle
      const handleCounts = new Map();
      this.dropdownSelections.forEach((handle) => {
        handleCounts.set(handle, (handleCounts.get(handle) || 0) + 1);
      });
      handleCounts.forEach((count, handle) => {
        let product = null;
        for (const cat of variant.categories) {
          product = cat.products.find(p => p.handle === handle);
          if (product) break;
        }
        if (product) {
          const sellingPlanId = this.findSellingPlanByName(product.sellingPlans, activePlanName);
          selections.push({
            externalProductId: String(product.productId || ''),
            externalVariantId: String(product.variantId),
            collectionId: String(product.collectionId || ''),
            quantity: count,
            sellingPlan: String(sellingPlanId)
          });
        }
      });
    } else {
      // Qty selections (predefined_builder and full_builder)
      this.selections.forEach((catSelections, categoryKey) => {
        const cat = variant.categories.find(c => c.key === categoryKey);
        catSelections.forEach((qty, handle) => {
          if (qty > 0) {
            const product = cat?.products.find(p => p.handle === handle);
            if (product) {
              const sellingPlanId = this.findSellingPlanByName(product.sellingPlans, activePlanName);
              selections.push({
                externalProductId: String(product.productId || ''),
                externalVariantId: String(product.variantId),
                collectionId: String(product.collectionId || ''),
                quantity: qty,
                sellingPlan: String(sellingPlanId)
              });
            }
          }
        });
      });
    }

    return selections;
  }

  findSellingPlanByName(sellingPlans, planName) {
    if (!sellingPlans?.length) return '';
    if (planName) {
      const match = sellingPlans.find(p => p.name === planName);
      if (match) return String(match.id);
    }
    return String(sellingPlans[0].id);
  }

  // ==========================================================================
  // Utils
  // ==========================================================================

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PackVariantBuilder());
} else {
  new PackVariantBuilder();
}
